import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserPlus, Stethoscope, Glasses, Receipt,
  FileBarChart2, ShieldCheck, Settings, ScrollText, Building2, ChevronLeft,
  ChevronRight, LogOut, Bell, Search, Menu, BookText, Database, Phone,
  Scissors, FlaskConical, Pill, BedDouble, FileBadge, MessageSquare, CalendarClock, Package,
  CreditCard, Wallet, BadgeIndianRupee, Mail, Ticket
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SubscriptionTimer from "@/components/SubscriptionTimer";


const ROLE_LABEL = {
  super_admin: "Super Admin", saas_admin: "SaaS Admin", saas_manager: "SaaS Manager", saas_user: "SaaS User",
  hospital_admin: "Hospital Admin",
  branch_admin: "Branch Admin", doctor: "Ophthalmologist",
  optometrist: "Optometrist", receptionist: "Receptionist",
  billing: "Billing", abdm_operator: "ABDM Operator", nurse: "Nurse",
};

function navForRole(role) {
  const SAAS_ROLES = ["super_admin", "saas_admin", "saas_manager", "saas_user"];
  if (SAAS_ROLES.includes(role)) {
    return [
      { to: "/app", icon: LayoutDashboard, label: "Overview", end: true },
      { to: "/app/superadmin/hospitals", icon: Building2, label: "Hospitals" },
      { to: "/app/superadmin/plans", icon: BookText, label: "Subscription Plans" },
      { to: "/app/superadmin/subscriptions", icon: BadgeIndianRupee, label: "Hospital Billing" },
      { to: "/app/superadmin/coupons", icon: Ticket, label: "Coupons" },
      { to: "/app/superadmin/payment-gateways", icon: CreditCard, label: "Payment Gateways" },
      { to: "/app/superadmin/saas-email", icon: Mail, label: "SaaS Email (SMTP)" },
      { to: "/app/superadmin/backups", icon: Database, label: "Backups & Dunning" },
      { to: "/app/superadmin/gst-settings", icon: Receipt, label: "GST Settings" },
      { to: "/app/superadmin/masters", icon: Database, label: "Master Library" },
      { to: "/app/superadmin/staff", icon: Users, label: "Internal Staff" },
      { to: "/app/audit", icon: ScrollText, label: "Audit Trail" },
    ];
  }
  const base = [
    { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, color: "text-brand", module: null },
    { to: "/app/patients", icon: Users, label: "Patients", color: "text-sky-300", module: "patients" },
    { to: "/app/patients/new", icon: UserPlus, label: "New Patient", color: "text-sky-300", module: "patients" },
    { to: "/app/opd", icon: Stethoscope, label: "OPD Queue", color: "text-teal-300", module: "opd" },
    { to: "/app/appointments", icon: CalendarClock, label: "Appointments", color: "text-cyan-300", module: "appointments" },
    { to: "/app/optometry", icon: Glasses, label: "Optometry", color: "text-fuchsia-300", module: "optometry" },
    { to: "/app/ot", icon: Scissors, label: "OT", color: "text-rose-300", module: "ot" },
    { to: "/app/ot/implants", icon: Package, label: "Implants", color: "text-pink-300", module: "implants" },
    { to: "/app/lab", icon: FlaskConical, label: "Lab", color: "text-violet-300", module: "lab" },
    { to: "/app/pharmacy", icon: Pill, label: "Pharmacy", color: "text-amber-300", module: "pharmacy" },
    { to: "/app/ipd", icon: BedDouble, label: "IPD / Day-care", color: "text-orange-300", module: "ipd" },
    { to: "/app/billing", icon: Receipt, label: "Billing", color: "text-indigo-300", module: "billing" },
    { to: "/app/panel", icon: FileBadge, label: "Panel / TPA", color: "text-lime-300", module: "panel" },
    { to: "/app/abdm", icon: ShieldCheck, label: "ABDM", color: "text-leaf-300", module: "abdm" },
    { to: "/app/reports", icon: FileBarChart2, label: "Reports", color: "text-emerald-300", module: "reports" },
  ];
  if (["hospital_admin", "branch_admin", "it_admin"].includes(role)) {
    base.push({ to: "/app/admin/users", icon: Users, label: "Users", color: "text-sky-300", module: "users" });
    base.push({ to: "/app/admin/branches", icon: Building2, label: "Branches", color: "text-slate-300", module: "branches" });
    base.push({ to: "/app/admin/masters", icon: Database, label: "Masters", color: "text-zinc-300", module: "masters" });
    base.push({ to: "/app/admin/reminders", icon: MessageSquare, label: "Reminders", color: "text-yellow-300", module: "reminders" });
    base.push({ to: "/app/my-plan", icon: Wallet, label: "My Plan", color: "text-emerald-300", module: "my_plan" });
    base.push({ to: "/app/admin/settings", icon: Settings, label: "Settings", color: "text-stone-300", module: "settings" });
  }
  base.push({ to: "/app/audit", icon: ScrollText, label: "Audit Trail", color: "text-stone-300", module: null });
  return base;
}

export default function Layout() {
  const { user, hospital, branch, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const items = navForRole(user.role);
  const eff = user.effective_modules || [];
  const allowAll = eff.includes("*") || eff.length === 0;
  const visibleItems = allowAll ? items : items.filter((it) => !it.module || eff.includes(it.module));
  const initials = (user.name || user.email).split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`${collapsed ? "w-16" : "w-64"} ${mobileOpen ? "fixed inset-y-0 left-0 z-40" : "hidden"} flex-shrink-0 flex-col bg-brand text-white transition-all duration-200 md:flex`}
      >
        <div className="flex flex-col items-start border-b border-white/10 px-5 pt-2 pb-3 relative">
          {!collapsed ? (
            <>
              {/* Logo left-aligned to match icon column. Container clips internal whitespace. */}
              <div className="h-[68px] w-44 overflow-hidden flex items-center">
                <img src={BRAND.logo} alt="CareO" className="brand-logo-white w-full drop-shadow-lg" />
              </div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85">EMR for Eye Care</div>
            </>
          ) : (
            <div className="h-10 w-10 overflow-hidden flex items-start justify-center mx-auto">
              <img src={BRAND.logo} alt="CareO" className="brand-logo-white -mt-1 w-full drop-shadow-lg" />
            </div>
          )}
          <button
            data-testid="sidebar-collapse-btn"
            className="absolute top-2 right-2 hidden rounded p-1 text-white/70 hover:bg-white/10 md:block"
            onClick={() => setCollapsed(v => !v)}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
          {visibleItems.map(({ to, icon: Icon, label, end, color }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={18} strokeWidth={2} className={color || "text-white/85"} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3 text-[10px] text-white/60">
          {!collapsed ? BRAND.footer : "©Steward"}
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-white px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-btn" className="md:hidden" onClick={() => setMobileOpen(true)}>
              <Menu size={20} />
            </button>
            <div className="hidden md:block">
              {hospital && (
                <div className="text-xs text-ink-muted">
                  <span className="font-medium text-ink">{hospital.name}</span>
                  {branch && <> · <span>{branch.name}</span></>}
                </div>
              )}
              <div className="font-heading text-lg font-semibold">Hello, {user.name.split(" ")[0]}</div>
            </div>
          </div>

          <div className="hidden flex-1 max-w-xl md:block" />

          <div className="flex items-center gap-3">
            <SubscriptionTimer userRole={user.role} />
            {/* Direct support / renewal helpline — always visible on the topbar
                so hospital staff can call for any query, complaint or plan
                renewal without hunting through Help pages. */}
            <a
              href="tel:+919616298162"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand-50 px-3 py-1 text-[11px] font-semibold text-brand hover:bg-brand-100"
              title="Any query / complaint / renewal — call CareO support"
              data-testid="topbar-support-phone"
            >
              <Phone size={12} className="text-brand" />
              <span className="hidden lg:inline text-ink-muted font-normal">Support:</span>
              <span className="font-mono">96162-98162</span>
            </a>
            {/* Mobile-only condensed variant — icon-only so it doesn't crowd the header. */}
            <a
              href="tel:+919616298162"
              className="md:hidden inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand"
              data-testid="topbar-support-phone-mobile"
            >
              <Phone size={14} />
            </a>
            <Badge data-testid="abdm-status-pill" variant="outline" className="border-leaf/40 bg-leaf-50 text-leaf-700">
              <ShieldCheck size={12} className="mr-1" /> ABDM Ready
            </Badge>
            <button className="rounded-full p-2 text-ink-muted hover:bg-secondary" data-testid="notifications-btn">
              <Bell size={18} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-secondary">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-brand text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden text-left md:block">
                    <div className="text-xs font-medium">{user.name}</div>
                    <div className="text-[10px] text-ink-muted">{ROLE_LABEL[user.role] || user.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/profile")} data-testid="menu-profile">
                  <Users size={14} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/audit")} data-testid="menu-audit">
                  <ScrollText size={14} className="mr-2" /> Audit Trail
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>

        <footer className="border-t border-border bg-white px-6 py-3 text-center text-[11px] text-ink-muted">
          {BRAND.footer} · NABH-ready · ABDM-compatible
        </footer>
      </div>

      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label="close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </div>
  );
}
